/**
 * @file    useCprTabCleanup.ts
 * @scope   CPR
 * @summary Side effects that must run when the TabNavigator pops the CPR Manage Programs views.
 * @consumes CprContext, programsManagerModel tab descriptors, filterValidation
 * @produces `handleNavigate` (TabNavigator callback) and `maxCrumbs` (breadcrumb depth)
 * @sideEffects Strips incomplete programs/filters from the CPR context and clears the drawer's
 *              CPR programs context.
 * @notes Composed with the other subtypes' cleanups by `useEngineTabNavigation`.
 */
import { useCprContext } from 'components/DiyWidgetEngine/Subtypes/CrossProgramRollup/state/CprContext';
import {
  CprPartitionFiltersTab,
  CprProgramFiltersTab,
  ProgramsManagerTab,
  isProgramIncomplete,
} from 'components/DiyWidgetEngine/Subtypes/CrossProgramRollup/Views/ProgramsManager/programsManagerModel';
import { stripIncompleteFilters } from 'components/DiyWidgetEngine/Subtypes/Enterprise/utils/validation/filterValidation';
import { useCprNavigation } from '../plugins/cpr/CprNavigationContext';
import { EngineTabCleanup, EngineTabCleanupParams } from '../plugins/types';

const CPR_FILTER_TAB_NAMES = [CprPartitionFiltersTab.name, CprProgramFiltersTab.name];

export function useCprTabCleanup({ currentTab }: EngineTabCleanupParams): EngineTabCleanup {
  const { state, dispatch } = useCprContext();
  const { cprProgramsContext, setCprProgramsContext } = useCprNavigation();

  /** Drops what the user left half-configured when the Manage Programs tab is popped. */
  const stripIncompleteRollupData = () => {
    const rollup = state.metrics.find((m) => m.id === cprProgramsContext?.rollupId);
    if (!rollup) return;
    dispatch({
      type: 'UPDATE_ROLLUP_METRIC',
      id: rollup.id,
      changes: {
        programs: rollup.programs
          .filter((p) => !isProgramIncomplete(p))
          .map((p) => ({ ...p, filters: stripIncompleteFilters(p.filters ?? []) })),
        partitionFilters: stripIncompleteFilters(rollup.partitionFilters ?? []),
      },
    });
  };

  const onNavigate = (tab: any, poppedTabs: any[]) => {
    if (poppedTabs.some((t) => t.name === ProgramsManagerTab.name)) stripIncompleteRollupData();

    // The context must survive when returning from a filters view so the card stays expanded.
    if (tab.name !== ProgramsManagerTab.name && !CPR_FILTER_TAB_NAMES.includes(tab.name)) {
      setCprProgramsContext(null);
    }
  };

  // WHY: the filters views show "Manage programs > Filters", like the Enterprise third-level views.
  const maxCrumbs = CPR_FILTER_TAB_NAMES.includes(currentTab.name) ? 2 : undefined;

  return { onNavigate, maxCrumbs };
}

export default useCprTabCleanup;
